import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDailyFigureById } from "../../../services/dailyFigureService";
import { InfoSection, InfoItem } from "../../../components/Info";
import { getStatusColor } from "../../../utils/helpers";

export default function DailyFigure() {
    const params = useParams()
    const [data, setData] = useState()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const navigate = useNavigate()

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true)
                const res = await getDailyFigureById(params.id)
                setData(res.data.data)
            } catch (err) {
                setError("Failed to load daily figure data")
                console.error("Error fetching data:", err)
            } finally {
                setLoading(false)
            }
        }
        
        fetchData()
    }, [params.id])

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <div className="text-red-600 font-semibold mb-2">{error}</div>
                <button 
                    onClick={() => navigate(-1)}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                >
                    ← Go back
                </button>
            </div>
        )
    }

    if (!data) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-500 text-lg">No data found</div>
                <button 
                    onClick={() => navigate(-1)}
                    className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
                >
                    ← Go back
                </button>
            </div>
        )
    }
    

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Improved Header */}
            <div className="mb-8">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                            <h1 className="text-2xl font-bold text-gray-900">Daily Figure Details</h1>
                            {data?.status && (
                                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(data.status)}`}>
                                    {data.status}
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-6 text-sm text-gray-600">
                            <span className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                                </svg>
                                Ticket ID: {data?.book_ticket_id}
                            </span>
                            {data?.date && (
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    {data.date}
                                </span>
                            )}
                        </div>
                    </div>
                    
                    <button 
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200 font-medium"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Back
                    </button>
                </div>
                <hr className="border-gray-200 mt-4" />
            </div>

            {/* Main Content */}
            <div className="space-y-8">
                {/* Bet Information */}
                <InfoSection title="Bet Information" subtitle="Core details of the betting ticket">
                    <InfoItem label="Website" value={data?.website} />
                    <InfoItem label="Account ID" value={data?.account_id} />
                    <InfoItem label="Booking Ticket ID" value={data?.book_ticket_id} />
                    <InfoItem label="Game No" value={data?.game_no} />
                    <InfoItem label="Sport" value={data?.sport} />
                    <InfoItem label="Bet Type" value={data?.bet_type} />
                    <InfoItem label="Play Type" value={data?.play_type} />
                    <InfoItem label="Status" value={data?.status} isStatus />
                </InfoSection>

                {/* Match Details */}
                <InfoSection title="Match Details" subtitle="Game and team information">
                    <InfoItem label="Team 1" value={data?.team_1} />
                    <InfoItem label="Team 2" value={data?.team_2} />
                    <InfoItem label="Team Bet On" value={data?.team_bet_on} />
                    <InfoItem label="Final Score" value={data?.final_score} />
                    <InfoItem label="Period" value={data?.period} />
                    <InfoItem label="Date" value={data?.date} />
                    <InfoItem label="Time" value={data?.time} />
                    <InfoItem label="Timezone" value={data?.timezone} />
                </InfoSection>

                {/* Betting Details */}
                <InfoSection title="Betting Details" subtitle="Odds, spread, and financial information">
                    <InfoItem label="Odds" value={data?.odds} />
                    <InfoItem label="Spread" value={data?.spread} />
                    <InfoItem label="Risk" value={data?.risk} />
                    <InfoItem label="Win" value={data?.win} />
                    <InfoItem label="Accepted" value={data?.accepted} />
                    <InfoItem label="Placed On" value={data?.placed_on} />
                </InfoSection>

                {/* Custom Data */}
                <InfoSection 
                    title="Custom Data" 
                    subtitle="This information added manually"
                    columns={3}
                >
                    <InfoItem label="Master Bet ID" value={data?.master_bet_id} />
                    <InfoItem label="Worker" value={data?.worker} />
                    <InfoItem label="Worker Free Roll" value={data?.worker_freeroll} />
                    <InfoItem label="Handicapper" value={data?.handicapper} />
                    <InfoItem label="Handicapper Risk" value={data?.handicapper_risk} />
                    <InfoItem label="Agent" value={data?.agent_id} />
                    <InfoItem label="Master Agent Risk" value={data?.master_agent_risk} />
                    <InfoItem label="Sub Agent Risk" value={data?.sub_agent_risk} />
                    <InfoItem label="Master Agent Freeroll" value={data?.master_agent_freeroll} />
                    <InfoItem label="Sub Agent Freeroll" value={data?.sub_agent_freeroll} />
                </InfoSection>

                {/* Notes */}
                {data?.notes && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                        <p className="text-gray-700 bg-gray-50 rounded-lg p-4 text-sm leading-relaxed">
                            {data.notes}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}